import React, { useState, useEffect } from "react";
import { CryptoAlertData } from "@crypto-trading/common/browser";
import { TrendingUp, TrendingDown } from "lucide-react";
import { useTokenLivePrice } from "@/hooks/useTokenLivePrice";
import { useInView } from "@/hooks/useInView";
import { TokenIcon } from "@/components/TokenIcon";

interface CryptoAlertTileProps {
  alert: CryptoAlertData;
  onViewPosition?: (positionId: string) => void;
  onDetailsClick?: () => void;
  onTrade?: (alert: CryptoAlertData) => void;
}

export const CryptoAlertTile: React.FC<CryptoAlertTileProps> = ({
  alert,
  onViewPosition,
  onDetailsClick,
  onTrade,
}) => {
  const isListing = alert.type === "new_coin";

  // Viewport detection - only subscribe to live prices when visible
  const [tileRef, isInView] = useInView<HTMLDivElement>({
    rootMargin: "50px",
    threshold: 0.1,
  });

  // Subscribe to live price updates when tile is visible
  const { price: livePrice, isSubscribed } = useTokenLivePrice(
    alert.currency,
    undefined,
    true // Only subscribe for listing alerts that are visible
  );

  // Track if we've waited too long for a price (timeout after 8 seconds)
  const [priceLoadTimeout, setPriceLoadTimeout] = useState(false);

  useEffect(() => {
    if (isInView && !livePrice) {
      setPriceLoadTimeout(false);
      const timer = setTimeout(() => {
        setPriceLoadTimeout(true);
      }, 8000); // 8 second timeout

      return () => clearTimeout(timer);
    }
  }, [isInView, livePrice]);

  // Use live price from WebSocket subscription
  const displayPrice = livePrice;

  // Loading state: true when tile is in view but we don't have a price yet (and haven't timed out)
  const isPriceLoading = isInView && !livePrice && !priceLoadTimeout;

  const formatPrice = (price: number | null) => {
    if (!price) return "Not available";
    return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}`;
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const d = new Date(date);
    const diffMs = now.getTime() - d.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return "just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const calculatePriceChangeToAlertPrice = (price: number | null) => {
    if (!alert.price || !price) return null;
    const change = ((price - alert.price) / alert.price) * 100;
    return change;
  };

  // Calculate price change using live/display price
  const priceChange = calculatePriceChangeToAlertPrice(displayPrice);
  const priceChangeToPosition = calculatePriceChangeToAlertPrice(
    alert.userPosition?.entryPrice || null
  );

  const getTierBadgeColor = () => {
    if (alert.tier === 1) return "text-yellow-400";
    if (alert.tier === 2) return "text-blue-400";
    return "text-gray-400";
  };

  const tierBadgeColor = getTierBadgeColor();
  const isClosedPosition = alert.userPosition?.status === "CLOSED";

  // Check if all price data is unavailable (no position)
  const allPriceDataUnavailable =
    !alert.userPosition &&
    !alert.price &&
    !displayPrice &&
    priceChange === null;

  // Get available exchanges
  const availableExchanges = [
    {
      name: "Binance",
      available: alert.binanceAvailable,
      color: "bg-yellow-800",
    },
    { name: "Bybit", available: alert.bybitAvailable, color: "bg-orange-800" },
    { name: "Gate", available: alert.gateAvailable, color: "bg-blue-800" },
    { name: "KuCoin", available: alert.kucoinAvailable, color: "bg-green-800" },
    {
      name: "Hyperliquid",
      available: alert.hyperliquidAvailable,
      color: "bg-purple-800",
    },
    { name: "Aster", available: alert.asterdexAvailable, color: "bg-pink-800" },
  ].filter((ex) => ex.available);

  return (
    <div
      ref={tileRef}
      className="bg-secondary rounded-lg p-3 border border-gray-500/30"
    >
      {/* Header with badges */}
      <div className="flex items-start justify-between border-b border-gray-500/30 pb-2">
        <div className="flex flex-col w-full">
          <div className="flex items-center justify-between gap-2 w-full">
            <div className="flex items-center gap-2">
              {isListing ? (
                <TrendingUp className="w-5 h-5 text-green-400" />
              ) : (
                <TrendingDown className="w-5 h-5 text-red-400" />
              )}
              <h3 className="text-lg font-bold text-text-primary">
                {alert.currency}
              </h3>
              <TokenIcon
                symbol={alert.currency}
                logo={alert.tokenMetadata?.logo}
                size="sm"
              />
            </div>
            <div className="flex items-center gap-2">
              {alert.hasAutotraded && (
                <span className="px-2 py-0.5 rounded-full text-xs bg-accent/10">
                  Autotraded 🤖
                </span>
              )}
              {alert.tier && (
                <span
                  className={`px-2 py-0.5 rounded-full text-xs bg-black/50 ${tierBadgeColor}`}
                >
                  Tier {"🔥".repeat(4 - alert.tier)}
                </span>
              )}
            </div>
          </div>
          {/* Exchange Info */}
          <p className="text-xs text-text-secondary mt-1">
            {`${alert.type === "new_coin" ? "Listed on" : "Delisted from"}`}:{" "}
            <span className="text-accent font-medium capitalize">
              {alert.exchange[0].toUpperCase() + alert.exchange.slice(1)}{" "}
              {alert.exchangeType || "spot"}
            </span>
            <span className="text-text-secondary/70">
              {" "}
              · {formatTimeAgo(alert.createdAt)}
            </span>
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      {allPriceDataUnavailable ? (
        <div className="pt-2 flex items-center justify-center min-h-[52px]">
          <p className="text-sm text-text-secondary">
            Not available for trading yet
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4 pt-2">
          {/* Alert Price */}
          <div className="border-r border-gray-500/30">
            <p className="text-xs text-text-secondary mb-1">
              {isClosedPosition ? "Position opened at:" : "Alert Price"}
            </p>
            <p className="text-sm text-text-primary">
              {formatPrice(
                isClosedPosition
                  ? (alert.userPosition?.entryPrice ?? null)
                  : alert.price
              )}
            </p>
          </div>

          {/* Current Price */}
          <div className="border-r border-gray-500/30">
            <div className="flex items-center gap-1 mb-1">
              <p className="text-xs text-text-secondary">Current Price</p>
              {isSubscribed && livePrice && (
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
              )}
            </div>
            {isPriceLoading ? (
              <div className="space-y-1">
                <div className="h-5 bg-gray-600/30 rounded animate-pulse w-20"></div>
                {alert.hasAutotraded && (
                  <div className="h-4 bg-gray-600/30 rounded animate-pulse w-12"></div>
                )}
              </div>
            ) : (
              <>
                <p
                  className={`text-sm text-text-primary ${isSubscribed && livePrice ? "transition-all duration-150" : ""}`}
                >
                  {formatPrice(displayPrice)}
                </p>
                {alert.hasAutotraded && priceChange !== null && (
                  <p
                    className={`text-xs mt-0.5 ${priceChange >= 0 ? "text-green-400" : "text-red-400"}`}
                  >
                    {priceChange >= 0 ? "+" : ""}
                    {priceChange.toFixed(2)}%
                  </p>
                )}
              </>
            )}
          </div>

          {/* Position Price or Change */}
          <div>
            {alert.userPosition ? (
              <>
                <p className="text-xs text-text-secondary mb-1">
                  {alert.userPosition.status === "OPEN"
                    ? "Position OPEN"
                    : "Position closed at"}
                </p>
                <p className="text-sm text-text-primary">
                  {formatPrice(
                    alert.userPosition.status === "CLOSED"
                      ? (alert.userPosition.exitPrice ?? null)
                      : alert.userPosition.entryPrice
                  )}
                </p>
                {priceChangeToPosition !== null &&
                  alert.userPosition.status === "OPEN" && (
                    <p
                      className={`text-xs mt-0.5 ${priceChangeToPosition >= 0 ? "text-green-400" : "text-red-400"}`}
                    >
                      {priceChangeToPosition >= 0 ? "+" : ""}
                      {priceChangeToPosition.toFixed(2)}%
                    </p>
                  )}
                {alert.userPosition.status === "CLOSED" && (
                  <p
                    className={`text-xs mt-0.5 ${alert.userPosition.pnlPercentage !== null && alert.userPosition.pnlPercentage !== undefined && alert.userPosition.pnlPercentage >= 0 ? "text-green-400" : "text-red-400"}`}
                  >
                    PnL: {alert.userPosition.pnlPercentage?.toFixed(2)}%
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="text-xs text-text-secondary mb-1">Change</p>
                {isPriceLoading ? (
                  <div className="h-5 bg-gray-600/30 rounded animate-pulse w-16"></div>
                ) : priceChange === null ? (
                  <p className="text-sm text-text-primary">Not available</p>
                ) : (
                  <div className="flex gap-1">
                    {priceChange >= 0 ? (
                      <img
                        src="/icons/profit.png"
                        alt="Trending Up"
                        className="w-6 h-4 object-contain"
                      />
                    ) : (
                      <img
                        src="/icons/loss.png"
                        alt="Trending Down"
                        className="w-6 h-4 object-contain"
                      />
                    )}
                    <div>
                      <p
                        className={`text-sm ${priceChange >= 0 ? "text-green-400" : "text-red-400"}`}
                      >
                        {priceChange >= 0 ? "+" : ""}
                        {priceChange.toFixed(2)}%
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2 mt-3 pt-3 border-t border-gray-500/30">
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (onDetailsClick) {
              onDetailsClick();
            }
          }}
          className={`${allPriceDataUnavailable ? "w-full" : "flex-1"} px-4 py-2 rounded-lg bg-gray-600/20 text-text-primary hover:bg-gray-600/30 transition-colors text-sm font-medium`}
        >
          Details
        </button>
        {!allPriceDataUnavailable && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (alert.userPosition && onViewPosition) {
                onViewPosition(alert.userPosition.id);
              } else if (!alert.userPosition && onTrade) {
                onTrade(alert);
              }
            }}
            className="flex-1 px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors text-sm font-medium"
          >
            {alert.userPosition ? "View Position" : "Trade"}
          </button>
        )}
      </div>
      {/* Available Exchanges Badges */}
      {availableExchanges.length > 0 && (
        <div className="flex flex-wrap justify-end items-baseline gap-1 mt-2">
          <span className="text-xs text-text-secondary mr-1">
            At {alert.type === "new_coin" ? "Listing" : "Delisting"} available
            on:
          </span>
          {availableExchanges.map((exchange) => (
            <span
              key={exchange.name}
              className={`px-2 py-0.5 rounded text-xs font-medium text-white ${exchange.color}`}
            >
              {exchange.name}
            </span>
          ))}
        </div>
      )}
      {availableExchanges.length === 0 && (
        <div className="flex flex-wrap justify-end items-baseline gap-1 mt-2">
          <span className="text-xs text-text-secondary mr-1">
            At {alert.type === "new_coin" ? "Listing" : "Delisting"} was not
            available on any exchanges
          </span>
        </div>
      )}
    </div>
  );
};
